/**
 * Wykonanie kontra rozkład: nadgodziny i pora nocna z FAKTÓW
 * (Etap 42, ADR-0043).
 *
 * Do tej pory o nadgodzinach i porze nocnej decydował rodzaj wpisu, który
 * pracownik sam wybrał w portalu. Od Etapu 39 mamy godziny „od–do", a od
 * Etapu 41 rozkład — więc jedno i drugie da się POLICZYĆ, a nie deklarować:
 *
 *  - **nadgodziny dobowe** to praca ponad dobowy wymiar wynikający
 *    z obowiązującego rozkładu (art. 151 § 1 k.p.); dzień zaplanowany jako
 *    wolny daje nadgodziny od pierwszej godziny,
 *  - **nadgodziny średniotygodniowe** to nadwyżka czasu przepracowanego nad
 *    wymiarem okresu, po odjęciu nadgodzin już policzonych dobowo
 *    (art. 151(1) § 2),
 *  - **dodatek 100 %** należy się za nadgodziny w nocy, w niedziele i święta
 *    niebędące dla pracownika dniami pracy oraz w dniu wolnym udzielonym
 *    w zamian; w każdym innym dniu **50 %** (art. 151(1) § 1),
 *  - **pora nocna** to 8 godzin między 21.00 a 7.00 (art. 151(7) § 1) —
 *    KTÓRE osiem, ustala pracodawca, więc okno jest konfiguracją, nie stałą.
 *
 * Reguła nadrzędna: liczba nadgodzin i godzin nocnych nigdy nie jest MNIEJSZA
 * od zadeklarowanej w zatwierdzonej ewidencji. Wpis przyjęty przez przełożonego
 * jest faktem, a niepełny rozkład albo brak godzin „od–do" nie unieważnia go.
 */
import { DAILY_REST_HOURS, rangeInterval } from './schedule'

/** Art. 151(7) § 1 k.p.: pora nocna obejmuje 8 godzin między 21.00 a 7.00. */
export const NIGHT_WINDOW_HOURS = 8
export const NIGHT_WINDOW_EARLIEST = '21:00'
export const NIGHT_WINDOW_LATEST = '07:00'

/**
 * Domyślne okno pory nocnej: 22.00–06.00. Ustawa go nie narzuca — wybiera je
 * pracodawca w regulaminie pracy — ale to wybór najczęstszy, więc firma, która
 * nie zmieniła ustawień, liczy nim, a nie zerem godzin nocnych.
 */
export const NIGHT_WINDOW_DEFAULT = { startsAt: '22:00', endsAt: '06:00' } as const

/** Dodatki z art. 151(1) § 1 k.p., w punktach bazowych wynagrodzenia. */
export const OVERTIME_RATE_DAY_BP = 5000
export const OVERTIME_RATE_NIGHT_BP = 10_000

/**
 * Systemy czasu pracy i okres rozliczeniowy (Etap 44, ADR-0045).
 *
 * Do tej pory produkt znał jeden system: podstawowy, 8 godzin na dobę,
 * okres rozliczeniowy równy miesiącowi. Kodeks pracy zna ich więcej i różnią
 * się dwiema rzeczami, które wchodzą prosto do arytmetyki nadgodzin:
 * DOBOWYM WYMIAREM, który wolno zaplanować, i DŁUGOŚCIĄ OKRESU, w którym
 * rozlicza się przeciętną normę tygodniową.
 *
 *  - podstawowy: 8 h na dobę, okres do 4 miesięcy (art. 129 § 1),
 *  - równoważny: do 12 h, okres do 1 miesiąca (art. 135 § 1) — a w szczególnie
 *    uzasadnionych przypadkach do 3, przy pracach zależnych od pory roku do 4
 *    (§ 2 i § 3),
 *  - równoważny przy dozorze urządzeń i pogotowiu: do 16 h (art. 136 § 1),
 *  - równoważny przy pilnowaniu mienia, ochronie osób i w strażach zakładowych:
 *    do 24 h (art. 137),
 *  - skrócony tydzień pracy i praca weekendowa: do 12 h, okres do 1 miesiąca,
 *    OBA na pisemny wniosek pracownika (art. 143 i art. 144),
 *  - zadaniowy: pracodawca ustala czas niezbędny do wykonania zadań
 *    (art. 140), a godzin pracy NIE EWIDENCJONUJE SIĘ (art. 149 § 2).
 *
 * Czego tu nie ma: ruchu ciągłego z art. 138 (przeciętnie 43 godziny
 * tygodniowo i dodatek za godziny od 9. do 12., czyli inna norma i inna
 * arytmetyka) oraz przerywanego z art. 139 (dwa bloki pracy w dobie
 * i wynagrodzenie za czas przerwy). Oba wymagają własnych reguł, a nie wpisu
 * w słowniku — patrz ADR-0045.
 */
export const WORK_TIME_SYSTEMS = [
  'podstawowy',
  'rownowazny',
  'rownowazny_dozor',
  'rownowazny_pilnowanie',
  'skrocony_tydzien',
  'weekendowy',
  'zadaniowy',
] as const
export type WorkTimeSystem = (typeof WORK_TIME_SYSTEMS)[number]

export interface WorkTimeSystemRules {
  /** Najdłuższy dobowy wymiar, jaki wolno ZAPLANOWAĆ w rozkładzie. */
  readonly maxDailyHours: number
  /** Okres rozliczeniowy bez trybu z art. 129 § 2 — w miesiącach. */
  readonly baseSettlementMonths: number
  /** Czy prowadzi się ewidencję GODZIN pracy (art. 149 § 2). */
  readonly tracksHours: boolean
  /**
   * Czy po zmianie w przedłużonym wymiarze należy się odpoczynek co najmniej
   * równy liczbie przepracowanych godzin (art. 136 § 2, stosowany odpowiednio
   * także przy art. 137).
   */
  readonly restEqualsShift: boolean
  /** Czy system wymaga pisemnego wniosku pracownika (art. 143 i art. 144). */
  readonly employeeRequest: boolean
}

export const WORK_TIME_SYSTEM_RULES: Record<WorkTimeSystem, WorkTimeSystemRules> = {
  podstawowy: {
    maxDailyHours: 8,
    baseSettlementMonths: 4,
    tracksHours: true,
    restEqualsShift: false,
    employeeRequest: false,
  },
  rownowazny: {
    maxDailyHours: 12,
    /* Art. 135 § 3 dopuszcza 4 miesiące przy pracach zależnych od pory roku. */
    baseSettlementMonths: 4,
    tracksHours: true,
    restEqualsShift: false,
    employeeRequest: false,
  },
  rownowazny_dozor: {
    maxDailyHours: 16,
    baseSettlementMonths: 4,
    tracksHours: true,
    restEqualsShift: true,
    employeeRequest: false,
  },
  rownowazny_pilnowanie: {
    maxDailyHours: 24,
    baseSettlementMonths: 4,
    tracksHours: true,
    restEqualsShift: true,
    employeeRequest: false,
  },
  skrocony_tydzien: {
    maxDailyHours: 12,
    baseSettlementMonths: 1,
    tracksHours: true,
    restEqualsShift: false,
    employeeRequest: true,
  },
  weekendowy: {
    maxDailyHours: 12,
    baseSettlementMonths: 1,
    tracksHours: true,
    restEqualsShift: false,
    employeeRequest: true,
  },
  zadaniowy: {
    /* Zadaniowy nie ma rozkładu, więc dobowego wymiaru nie planujemy wcale. */
    maxDailyHours: 8,
    baseSettlementMonths: 4,
    tracksHours: false,
    restEqualsShift: false,
    employeeRequest: false,
  },
}

/**
 * Górna granica okresu rozliczeniowego z art. 129 § 2: w każdym systemie wolno
 * go przedłużyć do 12 miesięcy, ale tylko w trybie tego przepisu — układem
 * zbiorowym albo porozumieniem, z zawiadomieniem inspektora pracy. System tego
 * nie sprawdza; pyta o potwierdzenie i zapisuje je jako fakt (ADR-0045).
 */
export const MAX_SETTLEMENT_MONTHS = 12

export interface SettlementPeriod {
  /** Pierwszy miesiąc okresu w postaci RRRR-MM. */
  readonly from: string
  /** Ostatni miesiąc okresu w postaci RRRR-MM. */
  readonly to: string
  readonly months: number
  /** Czy podany miesiąc jest OSTATNIM miesiącem okresu. */
  readonly isLast: boolean
}

function monthIndex(month: string): number {
  const [year, number_] = month.split('-').map(Number)
  return year! * 12 + (number_! - 1)
}

function monthOf(index: number): string {
  const year = Math.floor(index / 12)
  const number_ = index - year * 12 + 1
  return `${String(year)}-${String(number_).padStart(2, '0')}`
}

/**
 * Okres rozliczeniowy, w którym leży dany miesiąc. Okresy biegną od miesiąca
 * początkowego przyjętego w firmie, jeden za drugim — także w tył, bo lista
 * płac za miesiąc sprzed przyjęcia okresu musi się na czymś oprzeć.
 *
 * Przeciętną normę tygodniową rozlicza się na KONIEC okresu (art. 129 § 1),
 * dlatego `isLast` decyduje, czy w tym miesiącu liczymy nadgodziny
 * średniotygodniowe.
 */
export function settlementPeriod(
  month: string,
  startMonth: string,
  months: number,
): SettlementPeriod {
  const span = Math.min(MAX_SETTLEMENT_MONTHS, Math.max(1, Math.trunc(months)))
  const start = monthIndex(startMonth)
  const current = monthIndex(month)
  const index = Math.floor((current - start) / span)
  const fromIndex = start + index * span
  const toIndex = fromIndex + span - 1
  return {
    from: monthOf(fromIndex),
    to: monthOf(toIndex),
    months: span,
    isLast: current === toIndex,
  }
}

export interface TimeRange {
  /** Godzina „od" w formacie HH:MM. */
  readonly startsAt: string
  /** Godzina „do" w formacie HH:MM; mniejsza od „od" znaczy przez północ. */
  readonly endsAt: string
}

function minutes(time: string): number {
  const [hour, minute] = time.split(':').map(Number)
  return hour! * 60 + minute!
}

/** Zaokrąglenie godzin do dwóch miejsc — kwadranse i minuty bez ogonków. */
function round2(value: number): number {
  return Math.round(value * 100) / 100
}

/** Pełny etat w punktach bazowych (1/1 = 10 000). */
export const FULL_TIME_FTE_BP = 10_000

/**
 * Wymiar czasu pracy PRACOWNIKA w okresie (fala 7b, #25).
 *
 * Art. 130 k.p. daje wymiar dla pełnego etatu; osoba na części etatu pracuje
 * proporcjonalnie mniej, bo wymiar jest elementem umowy (art. 29 § 1 pkt 4).
 * Wymiar nominalny zostaje w zestawieniach obok — z niego rozlicza się
 * przeciętną normę tygodniową (decyzja właściciela 2026-09-28) — a ten jest
 * liczbą, którą widzi pracownik i od której liczy się praca ponad wymiar.
 */
export function employeeNormHours(normHours: number, fteBp: number): number {
  return round2((normHours * fteBp) / FULL_TIME_FTE_BP)
}

/** Długość zakresu w godzinach; przez północ liczy się jako ciągła praca. */
export function rangeHours(range: TimeRange): number {
  const start = minutes(range.startsAt)
  const end = minutes(range.endsAt)
  const span = end > start ? end - start : end + 24 * 60 - start
  return round2(span / 60)
}

/**
 * Czy okno pory nocnej jest zgodne z art. 151(7) § 1: dokładnie osiem godzin
 * i w całości między 21.00 a 7.00. Pracodawca wybiera, które osiem — ale nie
 * wybiera, ile ich jest.
 */
export function isNightWindow(range: TimeRange): boolean {
  if (rangeHours(range) !== NIGHT_WINDOW_HOURS) return false
  const start = minutes(range.startsAt)
  const end = minutes(range.endsAt)
  const earliest = minutes(NIGHT_WINDOW_EARLIEST)
  const latest = minutes(NIGHT_WINDOW_LATEST)
  /* Okno przechodzi przez północ: początek nie wcześniej niż 21.00... */
  if (end <= start) return start >= earliest && end <= latest
  /* ...albo mieści się w całości przed 7.00 rano. */
  return end <= latest
}

/**
 * Najdłuższa zmiana, którą przyjmujemy jako JEDNĄ. Art. 132 § 1 k.p. wymaga
 * 11 godzin nieprzerwanego odpoczynku w każdej dobie, więc na pracę zostaje
 * 13 godzin. Zakres dłuższy traktujemy jako pomyłkę w godzinach, a nie zmianę
 * przez północ: „16:00–07:30" to prawie na pewno odwrócone godziny, a nie
 * 15,5-godzinna zmiana. Systemów z art. 136 i 137 (zmiany 16- i 24-godzinne)
 * produkt nie obsługuje i mówi to wprost (ADR-0042, ADR-0043).
 */
export const MAX_SHIFT_HOURS = 13

/** Czy zakres „od–do" jest zmianą, którą wolno zapisać w ewidencji. */
export function isShiftRange(range: TimeRange): boolean {
  const length = rangeHours(range)
  return length > 0 && length <= MAX_SHIFT_HOURS
}

/** Przecięcie dwóch zakresów w minutach, na osi rozwiniętej przez północ. */
function overlapMinutes(
  work: { start: number; end: number },
  night: { start: number; end: number },
): number {
  const from = Math.max(work.start, night.start)
  const to = Math.min(work.end, night.end)
  return Math.max(0, to - from)
}

/**
 * Godziny pracy w porze nocnej. Zakres pracy i okno nocy rozwijamy na osi
 * dwóch dób, bo jedno i drugie może przechodzić przez północ — bez tego zmiana
 * 22.00–06.00 „nie miałaby" ani jednej godziny nocnej.
 */
export function nightHours(work: TimeRange, window: TimeRange): number {
  const workStart = minutes(work.startsAt)
  const workEnd = workStart + rangeHours(work) * 60
  const nightStart = minutes(window.startsAt)
  const nightEnd = nightStart + rangeHours(window) * 60

  const day = 24 * 60
  let total = 0
  /* Okno nocy z doby poprzedniej, bieżącej i następnej — praca może wejść w każde. */
  for (const shift of [-day, 0, day]) {
    total += overlapMinutes(
      { start: workStart, end: workEnd },
      { start: nightStart + shift, end: nightEnd + shift },
    )
  }
  return round2(total / 60)
}

/**
 * Czas wolny w zamian za pracę nadliczbową (art. 151(2) k.p., Etap 43).
 *
 * Dwie ścieżki i jeden skutek. Na wniosek pracownika złożony w postaci
 * papierowej albo elektronicznej pracodawca udziela czasu wolnego „w tym samym
 * wymiarze" (§ 1). Bez wniosku może udzielić go sam, ale wtedy w wymiarze
 * O POŁOWĘ WYŻSZYM i najpóźniej do końca okresu rozliczeniowego (§ 2).
 * W obu przypadkach pracownikowi NIE PRZYSŁUGUJE DODATEK za godziny
 * nadliczbowe (§ 3).
 *
 * Zapisujemy fakt: ile godzin wolnego UDZIELONO i kiedy. Ile godzin
 * nadliczbowych to pokrywa, wynika z przepisu, a nie z drugiego pola do
 * wpisania — dwie liczby wpisywane ręcznie rozjechałyby się przy pierwszej
 * pomyłce.
 */
export const OVERTIME_TIME_OFF_MODES = ['wniosek', 'pracodawca'] as const
export type OvertimeTimeOffMode = (typeof OVERTIME_TIME_OFF_MODES)[number]

/** Art. 151(2) § 2: bez wniosku czasu wolnego udziela się o połowę więcej. */
export const TIME_OFF_EMPLOYER_RATIO = 1.5

/**
 * Ile godzin nadliczbowych pokrywa udzielony czas wolny. Na wniosek — jedna za
 * jedną; bez wniosku — trzy godziny wolnego za dwie nadliczbowe, bo tylko tyle
 * spełnia wymóg „o połowę wyższego wymiaru".
 */
export function coveredOvertimeHours(
  mode: OvertimeTimeOffMode,
  grantedHours: number,
): number {
  const covered = mode === 'pracodawca' ? grantedHours / TIME_OFF_EMPLOYER_RATIO : grantedHours
  return round2(Math.max(0, covered))
}

/**
 * Czy czas wolny zmieścił się w terminie z art. 151(2) § 2 — do końca okresu
 * rozliczeniowego. Okresem jest u nas miesiąc (ADR-0042), więc granicą jest
 * ostatni dzień miesiąca, w którym powstały nadgodziny. Przy wniosku
 * pracownika (§ 1) ustawa terminu nie stawia, więc czas wolny wolno oddać
 * także w kolejnym miesiącu.
 */
export function isTimeOffInPeriod(
  mode: OvertimeTimeOffMode,
  overtimeMonth: string,
  grantedTo: string,
): boolean {
  if (mode === 'wniosek') return true
  return grantedTo.slice(0, 7) <= overtimeMonth
}

/**
 * Zdjęcie pokrytych godzin z obu stawek dodatku. Dzielimy je PROPORCJONALNIE:
 * ani pracownik, ani pracodawca nie wskazuje, które konkretnie godziny oddaje
 * czasem wolnym, a wybieranie najdroższych albo najtańszych byłoby decyzją na
 * czyjąś korzyść, której przepis nie przewiduje.
 */
export function reduceOvertimeByTimeOff(
  rate50Hours: number,
  rate100Hours: number,
  coveredHours: number,
): { readonly rate50Hours: number; readonly rate100Hours: number; readonly coveredHours: number } {
  const total = round2(rate50Hours + rate100Hours)
  const covered = Math.min(round2(Math.max(0, coveredHours)), total)
  if (total <= 0 || covered <= 0) {
    return { rate50Hours, rate100Hours, coveredHours: 0 }
  }
  const from50 = round2((covered * rate50Hours) / total)
  const from100 = round2(covered - from50)
  return {
    rate50Hours: round2(Math.max(0, rate50Hours - from50)),
    rate100Hours: round2(Math.max(0, rate100Hours - from100)),
    coveredHours: covered,
  }
}

/**
 * Dzień wolny w zamian za pracę w niedzielę, święto i w dniu wolnym z rozkładu
 * (art. 151(11) i art. 151(3) k.p., Etap 49).
 *
 * ADR-0043 i ADR-0044 nazwały tę lukę dwa razy: silnik znał już czas wolny za
 * nadgodziny (art. 151(2)), ale nie znał DNIA wolnego, który pracodawca ma
 * obowiązek zapewnić za pracę w niedzielę albo święto. To nie ta sama
 * instytucja i nie ten sam skutek:
 *
 *  - **art. 151(11) § 1** każe udzielić innego dnia wolnego: za pracę
 *    w NIEDZIELĘ w okresie sześciu dni kalendarzowych poprzedzających albo
 *    następujących po takiej niedzieli (pkt 1), a za pracę w ŚWIĘTO —
 *    w ciągu okresu rozliczeniowego (pkt 2),
 *  - **§ 2 i § 3**: jeżeli dnia wolnego udzielić się nie da, pracownikowi
 *    przysługuje DODATEK jak za godziny nadliczbowe (100 %) za każdą godzinę
 *    pracy w niedzielę albo w święto,
 *  - **art. 151(3)** dotyczy pracy w dniu wolnym wynikającym z rozkładu
 *    w przeciętnie pięciodniowym tygodniu pracy (u nas zwykle sobota):
 *    inny dzień wolny do końca okresu rozliczeniowego, w terminie uzgodnionym
 *    z pracownikiem.
 *
 * Zapisujemy FAKT: który dzień pracy jest rekompensowany i kiedy oddano dzień
 * wolny. Powód — niedziela, święto albo dzień wolny z rozkładu — wynika
 * z kalendarza i z rozkładu, więc nie jest polem do wyboru: gdyby był, ten sam
 * dzień dałoby się rozliczyć raz jako niedzielę, a raz jako sobotę, z dwoma
 * różnymi terminami.
 */

/** Art. 151(11) § 1 pkt 1: sześć dni kalendarzowych przed albo po niedzieli. */
export const SUNDAY_DAY_OFF_WINDOW_DAYS = 6

/**
 * Art. 151(12) k.p.: pracownik pracujący w niedziele ma korzystać co najmniej
 * raz na CZTERY tygodnie z niedzieli wolnej od pracy.
 */
export const FREE_SUNDAY_EVERY_SUNDAYS = 4

/** Za co należy się dzień wolny — fakt z kalendarza i z rozkładu, nie wybór. */
export const DAY_OFF_REASONS = ['niedziela', 'swieto', 'dzien_wolny_rozkladu'] as const
export type DayOffReason = (typeof DAY_OFF_REASONS)[number]

/**
 * Powód wyprowadzony z dnia pracy. Święto ma pierwszeństwo przed niedzielą:
 * święto przypadające w niedzielę jest jednym dniem wolnym, a termin z pkt 2
 * (okres rozliczeniowy) jest dla pracownika korzystniejszy niż sześć dni.
 */
export function dayOffReason(day: {
  readonly sunday: boolean
  readonly holiday: boolean
}): DayOffReason {
  if (day.holiday) return 'swieto'
  if (day.sunday) return 'niedziela'
  return 'dzien_wolny_rozkladu'
}

/** Data przesunięta o podaną liczbę dni kalendarzowych. */
function shiftDays(date: string, days: number): string {
  const shifted = new Date(`${date}T00:00:00Z`)
  shifted.setUTCDate(shifted.getUTCDate() + days)
  return shifted.toISOString().slice(0, 10)
}

/**
 * Okno terminu, w którym wolno oddać dzień wolny. Dla niedzieli liczy się
 * SZEŚĆ DNI w obie strony (art. 151(11) § 1 pkt 1) — dzień wolny może więc
 * wypaść przed pracą, a nie tylko po niej. Dla święta i dla dnia wolnego
 * z rozkładu granicą jest koniec okresu rozliczeniowego (art. 151(11) § 1
 * pkt 2 i art. 151(3)).
 */
export function dayOffWindow(input: {
  readonly reason: DayOffReason
  readonly workDate: string
  readonly period: SettlementPeriod
}): { readonly from: string; readonly to: string } {
  const periodEnd = monthLastDay(input.period.to)
  if (input.reason === 'niedziela') {
    return {
      from: shiftDays(input.workDate, -SUNDAY_DAY_OFF_WINDOW_DAYS),
      to: shiftDays(input.workDate, SUNDAY_DAY_OFF_WINDOW_DAYS),
    }
  }
  return { from: `${input.period.from}-01`, to: periodEnd }
}

/** Ostatni dzień miesiąca RRRR-MM. */
function monthLastDay(month: string): string {
  const [year, number_] = month.split('-').map(Number)
  return new Date(Date.UTC(year!, number_!, 0)).toISOString().slice(0, 10)
}

/**
 * Czy dzień wolny zmieścił się w terminie z przepisu. Dzień wolny oddany po
 * terminie jest faktem — pracownik go dostał — ale obowiązku z art. 151(11)
 * § 1 nie wypełnia, więc dodatek z § 2 albo § 3 zostaje należny. Tak samo
 * zachował się Etap 43 przy czasie wolnym po okresie rozliczeniowym.
 */
export function isDayOffInTerm(input: {
  readonly reason: DayOffReason
  readonly workDate: string
  readonly grantedOn: string
  readonly period: SettlementPeriod
}): boolean {
  const window = dayOffWindow(input)
  return input.grantedOn >= window.from && input.grantedOn <= window.to
}

/** Dzień pracy w niedzielę albo święto w postaci, w jakiej liczy się dodatek. */
export interface SundayWorkDay {
  readonly date: string
  readonly reason: DayOffReason
  /**
   * Godziny pracy tego dnia, za które NIE zapłacono już dodatku za nadgodziny.
   * Praca w niedzielę poza rozkładem jest nadgodziną z dodatkiem 100 %
   * (art. 151(1) § 1 pkt 1) — dodatek z art. 151(11) § 2 nie może się na nią
   * nałożyć, bo to ten sam dodatek za tę samą godzinę.
   */
  readonly hours: number
}

/**
 * Godziny pracy w niedziele i święta, którym nie odpowiada dzień wolny
 * udzielony w terminie — za nie należy się dodatek 100 % (art. 151(11) § 2
 * i § 3). Dzień wolny za pracę w dniu wolnym z rozkładu (art. 151(3)) dodatku
 * nie dotyczy: tam kompensata idzie przez nadgodziny, więc te dni tu nie
 * wchodzą.
 */
export function uncompensatedSundayHours(
  days: readonly SundayWorkDay[],
  compensated: readonly string[],
): number {
  const granted = new Set(compensated)
  return round2(
    days
      .filter((day) => day.reason !== 'dzien_wolny_rozkladu' && !granted.has(day.date))
      .reduce((total, day) => total + Math.max(0, day.hours), 0),
  )
}

/**
 * Art. 151(12) k.p.: czy pracownik przepracował cztery niedziele z rzędu.
 * Liczymy z niedziel, które znamy — a znamy je z ewidencji jednego okresu,
 * więc ciąg przechodzący przez granicę okresu zostaje niezauważony i mówimy
 * o tym wprost w ADR. Wynikiem jest ostatnia niedziela ciągu, żeby dało się
 * powiedzieć KTÓRA już nie mogła być pracująca.
 */
export function workedSundayStreak(
  sundays: readonly { readonly date: string; readonly worked: boolean }[],
): string | null {
  const ordered = [...sundays].toSorted((left, right) => (left.date < right.date ? -1 : 1))
  let streak = 0
  for (const sunday of ordered) {
    streak = sunday.worked ? streak + 1 : 0
    if (streak >= FREE_SUNDAY_EVERY_SUNDAYS) return sunday.date
  }
  return null
}

/**
 * Dyżur (art. 151(5) k.p., Etap 50).
 *
 * ADR-0043 nazwał tę lukę wprost: silnik nie znał dyżuru. A dyżur rozlicza się
 * inaczej niż wszystko, co system liczył do tej pory:
 *
 *  - **§ 1**: pracodawca może zobowiązać pracownika do pozostawania poza
 *    normalnymi godzinami pracy w gotowości do pracy — w zakładzie albo
 *    w innym miejscu wyznaczonym przez pracodawcę,
 *  - **§ 2**: czasu dyżuru NIE wlicza się do czasu pracy, jeżeli pracownik
 *    nie wykonywał pracy; czas dyżuru nie może naruszać prawa do odpoczynku
 *    z art. 132 i 133,
 *  - **§ 3**: za czas dyżuru — Z WYJĄTKIEM dyżuru pełnionego W DOMU —
 *    przysługuje czas wolny w wymiarze odpowiadającym długości dyżuru, a gdy
 *    udzielić go nie sposób, wynagrodzenie wynikające z osobistego
 *    zaszeregowania,
 *  - **§ 4**: § 3 nie stosuje się do pracowników zarządzających w imieniu
 *    pracodawcy zakładem pracy.
 *
 * Dyżur nie jest więc ani czasem pracy, ani nadgodziną: to trzecia kategoria,
 * z własną rekompensatą i własnymi wyjątkami.
 */

/** Gdzie pełniono dyżur — w domu rekompensata nie przysługuje (§ 3). */
export const DUTY_PLACES = ['zaklad', 'dom'] as const
export type DutyPlace = (typeof DUTY_PLACES)[number]

/**
 * Ile godzin doby wolno łącznie zająć pracą i dyżurem poza domem, żeby zostało
 * 11 godzin nieprzerwanego odpoczynku (art. 132 § 1 w związku z art. 151(5)
 * § 2): 24 − 11 = 13. Dyżur w domu tej granicy nie rusza — w domu pracownik
 * odpoczywa, choć pozostaje w gotowości.
 */
export const DUTY_MAX_ENGAGED_HOURS = 24 - DAILY_REST_HOURS

export interface DutyInput {
  readonly place: DutyPlace
  /** Długość dyżuru w godzinach. */
  readonly hours: number
  /**
   * Godziny pracy WYKONANEJ w czasie dyżuru. Te wchodzą do czasu pracy
   * (art. 151(5) § 2 zdanie pierwsze) i rozliczają się jak praca — zwykle jako
   * nadgodziny, bo dyżur jest poza normalnymi godzinami. Z rekompensaty za
   * gotowość wypadają, żeby ta sama godzina nie była płatna dwa razy.
   */
  readonly workedHours: number
  /** Czas wolny już udzielony za ten dyżur (art. 151(5) § 3). */
  readonly timeOffHours: number
  /** Pracownik zarządzający w imieniu pracodawcy zakładem pracy (§ 4). */
  readonly managing: boolean
}

export interface DutyResult {
  /** Godziny samej gotowości: dyżur minus praca wykonana w jego czasie. */
  readonly standbyHours: number
  /** Ile z nich pokrył czas wolny. */
  readonly timeOffHours: number
  /** Ile zostaje do zapłaty stawką osobistego zaszeregowania. */
  readonly paidHours: number
  /** Dlaczego rekompensata nie przysługuje; `null` = przysługuje. */
  readonly excluded: 'dom' | 'zarzadzajacy' | null
}

/**
 * Rekompensata za dyżur. Kolejność jest z przepisu: najpierw czas wolny
 * „w wymiarze odpowiadającym długości dyżuru", a dopiero „w razie braku
 * możliwości udzielenia czasu wolnego" — wynagrodzenie. Dlatego zapłata liczy
 * się z RESZTY, której czas wolny nie pokrył, a nie zamiast niego.
 */
export function dutyCompensation(input: DutyInput): DutyResult {
  const standbyHours = round2(Math.max(0, input.hours - Math.max(0, input.workedHours)))
  const excluded = input.place === 'dom' ? 'dom' : input.managing ? 'zarzadzajacy' : null
  if (excluded !== null) {
    return { standbyHours, timeOffHours: 0, paidHours: 0, excluded }
  }
  const timeOffHours = round2(Math.min(Math.max(0, input.timeOffHours), standbyHours))
  return {
    standbyHours,
    timeOffHours,
    paidHours: round2(Math.max(0, standbyHours - timeOffHours)),
    excluded: null,
  }
}

/**
 * Ile godzin brakuje do odpoczynku dobowego, gdy praca i dyżur poza domem
 * zajmują razem więcej niż 13 godzin doby (art. 151(5) § 2). Zero znaczy, że
 * odpoczynek się mieści.
 *
 * Liczymy z SUM, nie z położenia godzin na zegarze: dyżur zaczyna się „poza
 * normalnymi godzinami pracy", ale kiedy dokładnie — wie tylko grafik, którego
 * dyżur nie jest częścią. Granica sumaryczna wiąże zawsze, a o ułożeniu
 * godzin mówimy w ADR.
 */
export function dutyRestShortageHours(input: {
  readonly place: DutyPlace
  readonly hours: number
  readonly workedHours: number
}): number {
  if (input.place === 'dom') return 0
  const engaged = round2(Math.max(0, input.hours) + Math.max(0, input.workedHours))
  return round2(Math.max(0, engaged - DUTY_MAX_ENGAGED_HOURS))
}

/**
 * Praca w czasie dyżuru bez wpisu w ewidencji (fala 7b, grupa 50; ADR-0051
 * pkt 2). Praca z dyżuru rozlicza się jak praca (art. 151(5) § 2 zd. 1), ale do
 * czasu pracy wchodzi dopiero wpisem w ewidencji. Dyżur leży poza normalnymi
 * godzinami, więc praca w jego trakcie powinna się pojawić jako godziny ponad
 * rozkład dnia — brakuje tego, czego ponad rozkład nie ma.
 */
export function dutyWorkNotRecordedHours(input: {
  readonly dutyWorkedHours: number
  readonly dayWorkedHours: number
  readonly dayPlannedHours: number
}): number {
  const beyondPlan = Math.max(0, input.dayWorkedHours - input.dayPlannedHours)
  return round2(Math.max(0, input.dutyWorkedHours - beyondPlan))
}

/** Dzień ewidencji do sprawdzenia odpoczynku dobowego. */
export interface RestCheckDay {
  readonly date: string
  readonly worked: readonly TimeRange[]
  readonly workedHours: number
}

/** Doba pracownicza z odpoczynkiem krótszym niż 11 godzin. */
export interface RestShortage {
  readonly date: string
  /** Najdłuższa nieprzerwana przerwa w dobie, w godzinach. */
  readonly restHours: number
}

/**
 * Odpoczynek dobowy z ewidencji (fala 7b, grupa 175; art. 132 § 1 k.p.).
 *
 * Doba pracownicza liczy się od godziny rozpoczęcia pracy (art. 128 § 3 pkt 1),
 * więc od początku każdego zakresu „od–do" bierzemy 24 godziny i szukamy w nich
 * najdłuższej przerwy bez pracy; krótsza niż 11 godzin to naruszenie. Zakres
 * przez północ trwa do rana — ta sama oś co rozkład (`rangeInterval`). Dzień
 * bez godzin „od–do" sprawdzamy sumą: więcej niż 13 godzin pracy nie zostawia
 * 11 godzin odpoczynku w dobie. Dni podane tylko jako kontekst (np. ostatni
 * dzień poprzedniego miesiąca) wynik zwraca, a wywołujący je odfiltrowuje.
 */
export function workRestShortages(days: readonly RestCheckDay[]): readonly RestShortage[] {
  const intervals = days
    .flatMap((day) => day.worked.map((range) => ({ date: day.date, ...rangeInterval(day.date, range) })))
    .toSorted((left, right) => left.start - right.start)
  const byDate = new Map<string, number>()
  const note = (date: string, restHours: number): void => {
    const current = byDate.get(date)
    if (current === undefined || restHours < current) byDate.set(date, restHours)
  }

  const dayMinutes = 24 * 60
  for (const [index, first] of intervals.entries()) {
    const windowEnd = first.start + dayMinutes
    let cursor = first.start
    let longest = 0
    for (const other of intervals.slice(index)) {
      if (other.start >= windowEnd) break
      if (other.start > cursor) longest = Math.max(longest, other.start - cursor)
      cursor = Math.max(cursor, Math.min(other.end, windowEnd))
    }
    longest = Math.max(longest, windowEnd - cursor)
    if (longest < DAILY_REST_HOURS * 60) note(first.date, round2(longest / 60))
  }

  for (const day of days) {
    if (day.worked.length > 0 || day.workedHours <= MAX_SHIFT_HOURS) continue
    note(day.date, round2(Math.max(0, 24 - day.workedHours)))
  }
  return [...byDate].map(([date, restHours]) => ({ date, restHours })).toSorted((a, b) =>
    a.date < b.date ? -1 : 1,
  )
}

/**
 * Ryczałt za nadgodziny i za pracę w nocy (art. 151(1) § 4 i art. 151(8) § 2
 * k.p., Etap 51).
 *
 * ADR-0043 nazwał tę lukę wprost, a ADR-0051 powtórzył ją przy dyżurze.
 * Ustawa daje dwie osobne możliwości, obie z tym samym warunkiem:
 *
 *  - **art. 151(1) § 4** — w stosunku do pracowników wykonujących STALE pracę
 *    POZA ZAKŁADEM pracy wynagrodzenie wraz z dodatkiem za nadgodziny może być
 *    zastąpione ryczałtem, którego wysokość odpowiada przewidywanemu wymiarowi
 *    pracy w godzinach nadliczbowych,
 *  - **art. 151(8) § 2** — w stosunku do pracowników wykonujących pracę
 *    w porze nocnej stale poza zakładem pracy dodatek nocny może być zastąpiony
 *    ryczałtem odpowiadającym przewidywanemu wymiarowi pracy w porze nocnej.
 *
 * Trzecia konsekwencja jest w innym artykule: **art. 149 § 2** zwalnia
 * z ewidencjonowania godzin pracy pracowników objętych zadaniowym czasem pracy,
 * pracowników zarządzających zakładem ORAZ pracowników otrzymujących ryczałt za
 * godziny nadliczbowe albo za pracę w nocy. Do Etapu 51 znaliśmy tylko pierwszą
 * grupę (ADR-0045) — ryczałt domyka przepis.
 */

/** Warunek obu ryczałtów: stała praca poza zakładem pracy. */
export interface LumpSumSettings {
  /** Czy pracownik wykonuje STALE pracę poza zakładem pracy. */
  readonly outsideWorkplace: boolean
  /** Miesięczny ryczałt za nadgodziny w groszach; `null` = brak ryczałtu. */
  readonly overtimeLumpSumGr: number | null
  /** Miesięczny ryczałt za pracę w nocy w groszach; `null` = brak ryczałtu. */
  readonly nightLumpSumGr: number | null
}

export const NO_LUMP_SUM: LumpSumSettings = {
  outsideWorkplace: false,
  overtimeLumpSumGr: null,
  nightLumpSumGr: null,
}

/**
 * Czy ryczałt wolno w ogóle zastosować. Oba przepisy stawiają ten sam warunek
 * — stała praca poza zakładem — i to warunek DOPUSZCZALNOŚCI, nie wygody:
 * ryczałt dla pracownika przy biurku nie jest ryczałtem z art. 151(1) § 4,
 * tylko dodatkiem o dowolnej nazwie.
 */
export function isLumpSumAllowed(settings: LumpSumSettings): boolean {
  return settings.outsideWorkplace
}

/** Czy pracownik ma którykolwiek z dwóch ryczałtów. */
export function hasLumpSum(settings: LumpSumSettings): boolean {
  return settings.overtimeLumpSumGr !== null || settings.nightLumpSumGr !== null
}

/**
 * Ile brakuje w ryczałcie do kwoty, która wyszłaby z godzin. Ryczałt ma
 * odpowiadać PRZEWIDYWANEMU wymiarowi pracy — gdy wykonanie znacząco go
 * przewyższa, pracownikowi należy się różnica (utrwalone orzecznictwo).
 * Systemowi nie wolno tego rozstrzygać za człowieka: liczy różnicę i mówi
 * o niej ostrzeżeniem (ADR-0052).
 */
export function lumpSumShortfallGr(computedGr: number, lumpSumGr: number): number {
  return Math.max(0, Math.round(computedGr) - Math.round(lumpSumGr))
}

/** Powód, dla którego godzin pracy się nie ewidencjonuje (art. 149 § 2 k.p.). */
export const WORK_HOURS_EXEMPTIONS = ['zadaniowy', 'zarzadzajacy', 'ryczalt'] as const
export type WorkHoursExemption = (typeof WORK_HOURS_EXEMPTIONS)[number]

/**
 * Który z trzech powodów art. 149 § 2 k.p. wyłącza ewidencję godzin (fala 7b,
 * grupa 81): zadaniowy czas pracy, pracownik zarządzający zakładem albo
 * ryczałt za nadgodziny lub pracę w nocy; `null` = godziny się ewidencjonuje.
 * Kolejność ma znaczenie tylko dla komunikatu — system wyłącza każdy z nich.
 */
export function workHoursExemption(input: {
  readonly system: WorkTimeSystem
  readonly managingEmployee: boolean
  readonly lumpSum: LumpSumSettings
}): WorkHoursExemption | null {
  if (!WORK_TIME_SYSTEM_RULES[input.system].tracksHours) return 'zadaniowy'
  if (input.managingEmployee) return 'zarzadzajacy'
  return hasLumpSum(input.lumpSum) ? 'ryczalt' : null
}

/** Czy w tym przypadku ewidencjonuje się godziny pracy (art. 149 § 2 k.p.). */
export function tracksWorkHours(input: {
  readonly system: WorkTimeSystem
  readonly managingEmployee: boolean
  readonly lumpSum: LumpSumSettings
}): boolean {
  return workHoursExemption(input) === null
}

/**
 * Granice pracy nadliczbowej (Etap 45, ADR-0046).
 *
 * Dwie liczby, których do tej pory nie pilnowaliśmy, a ADR-0043 i ADR-0045
 * nazwały je wprost jako otwarte:
 *
 *  - **150 godzin w roku kalendarzowym** dla poszczególnego pracownika
 *    (art. 151 § 3) — ale tylko za nadgodziny „w związku ze szczególnymi
 *    potrzebami pracodawcy" z § 1 pkt 2. Godziny z akcji ratowniczej albo
 *    usuwania awarii (§ 1 pkt 1) do limitu NIE wchodzą. Układ zbiorowy,
 *    regulamin pracy albo umowa mogą ustalić inną liczbę (§ 4).
 *  - **przeciętnie 48 godzin tygodniowo** razem z nadgodzinami w przyjętym
 *    okresie rozliczeniowym (art. 131 § 1). Ograniczenie nie dotyczy
 *    pracowników zarządzających w imieniu pracodawcy zakładem pracy (§ 2).
 *
 * Obie granice są granicami DOPUSZCZALNOŚCI, nie zapłaty: przekroczone
 * nadgodziny trzeba i tak wynagrodzić, a naruszenie jest wykroczeniem
 * z art. 281 § 1 pkt 5. Dlatego liczymy je i mówimy o nich, ale nie odbieramy
 * ani złotówki (ADR-0046).
 */

/** Art. 151 § 3 k.p. — roczny limit nadgodzin ze szczególnych potrzeb. */
export const ANNUAL_OVERTIME_LIMIT_HOURS = 150

/**
 * Praktyczny pułap innej liczby z art. 151 § 4: 8 godzin razy 52 tygodnie.
 * Wyższej nie da się pogodzić z przeciętną 48-godzinną granicą z art. 131 § 1,
 * więc wyżej nie pozwalamy zapisać.
 */
export const ANNUAL_OVERTIME_LIMIT_MAX_HOURS = 416

/** Art. 131 § 1 k.p. — przeciętny tygodniowy czas pracy z nadgodzinami. */
export const WEEKLY_HOURS_WITH_OVERTIME_LIMIT = 48

/** Ile godzin wolno przepracować w okresie o tej długości (art. 131 § 1). */
export function periodHoursLimit(days: number): number {
  return Math.round(((WEEKLY_HOURS_WITH_OVERTIME_LIMIT * Math.max(0, days)) / 7) * 100) / 100
}

/** Przeciętny tygodniowy czas pracy okresu — liczba, którą ocenia art. 131 § 1. */
export function averageWeeklyHours(workedHours: number, days: number): number {
  if (days <= 0) return 0
  return Math.round(((workedHours * 7) / days) * 100) / 100
}

export interface AnnualOvertimeState {
  /** Nadgodziny wchodzące do limitu: wszystkie minus godziny z § 1 pkt 1. */
  readonly countedHours: number
  readonly limitHours: number
  /** Ile jeszcze zostało; zero, gdy limit wyczerpany. */
  readonly remainingHours: number
  /** Ile godzin ponad limit — podstawa wykroczenia z art. 281 § 1 pkt 5. */
  readonly exceededHours: number
}

/**
 * Stan rocznego limitu z art. 151 § 3. Godziny akcji ratowniczej i usuwania
 * awarii odejmujemy PRZED porównaniem, bo przepis liczy tylko nadgodziny
 * ze szczególnych potrzeb pracodawcy — ale ich liczba jest faktem wpisanym
 * przez kadry, nie domysłem silnika.
 */
export function annualOvertimeState(input: {
  readonly overtimeHours: number
  readonly rescueHours: number
  readonly limitHours: number
}): AnnualOvertimeState {
  const counted = Math.max(0, Math.round((input.overtimeHours - input.rescueHours) * 100) / 100)
  const limit = Math.max(0, input.limitHours)
  return {
    countedHours: counted,
    limitHours: limit,
    remainingHours: Math.max(0, Math.round((limit - counted) * 100) / 100),
    exceededHours: Math.max(0, Math.round((counted - limit) * 100) / 100),
  }
}

export interface WorkedDay {
  readonly date: string
  /**
   * Zakresy godzin „od–do" z wpisów ewidencji. Pusta lista = wpisy bez zegara,
   * z których pory nocnej wyznaczyć nie można.
   */
  readonly worked: readonly TimeRange[]
  /**
   * Suma godzin pracy z ewidencji — WSZYSTKIE rodzaje pracy, także wpisy
   * oznaczone jako nadgodziny. Karta ewidencji pokazuje je osobną kolumną
   * (§ 6 pkt 1 lit. a), ale porównanie z rozkładem wymaga jednej liczby.
   */
  readonly workedHours: number
  /** Nadgodziny ZADEKLAROWANE rodzajem wpisu — dolna granica wyniku. */
  readonly declaredOvertimeHours: number
  /** Godziny nocne ZADEKLAROWANE rodzajem wpisu — dolna granica wyniku. */
  readonly declaredNightHours: number
  /** Zaplanowany czas pracy z rozkładu; `null` = dzień planowany wolny. */
  readonly planned: TimeRange | null
  /** Czy dzień jest objęty rozkładem — bez niego nie ma od czego liczyć. */
  readonly scheduled: boolean
  /** Czy dzień jest świętem ustawowo wolnym od pracy. */
  readonly holiday: boolean
  /** Czy dzień jest niedzielą. */
  readonly sunday: boolean
}

export interface DayReview {
  readonly date: string
  readonly plannedHours: number
  readonly workedHours: number
  readonly nightHours: number
  /** Nadgodziny dobowe: praca ponad wymiar wynikający z rozkładu. */
  readonly overtimeHours: number
  /** Dodatek za te nadgodziny w punktach bazowych: 5000 albo 10 000. */
  readonly overtimeRateBp: number | null
  /** Czy dzień był w rozkładzie wolny — wtedy nadgodziny od pierwszej godziny. */
  readonly plannedOff: boolean
  /** Czy wynik dnia wziął się z deklaracji, bo rozkład dał mniej godzin. */
  readonly fromDeclaration: boolean
  /** Czy dzień ma pracę bez godzin „od–do" — pora nocna z deklaracji. */
  readonly clockMissing: boolean
  readonly sunday: boolean
  readonly holiday: boolean
  /**
   * Godziny pracy w niedzielę albo święto, za które nie zapłacono dodatku za
   * nadgodziny — czyli te z rozkładu. Za nie należy się dzień wolny
   * (art. 151(11) § 1), a bez niego dodatek 100 % (§ 2 i § 3). Godziny poza
   * rozkładem są już nadgodzinami po 100 %, więc tu nie wchodzą: ten sam
   * dodatek nie należy się dwa razy za tę samą godzinę (Etap 49, ADR-0050).
   */
  readonly sundayHolidayHours: number
}

/**
 * Zestawienie dnia: ile zaplanowano, ile przepracowano, ile z tego w nocy
 * i ile ponad rozkład. Dodatek rozstrzygamy po art. 151(1) § 1: noc, niedziela
 * i święto niebędące dniem pracy dają 100 %, wszystko inne 50 %.
 */
export function reviewDay(day: WorkedDay, window: TimeRange): DayReview {
  const plannedHours = day.planned === null ? 0 : rangeHours(day.planned)
  const clockMissing = day.workedHours > 0 && day.worked.length === 0

  /*
   * Pora nocna z okna, gdy znamy godziny; bez nich zostaje deklaracja. Nigdy
   * nie mniej niż zadeklarowano: wpis „noc" bez zegara opisuje te same godziny,
   * a nie inne, więc bierzemy większą z dwóch liczb, a nie ich sumę.
   */
  const computedNight = day.worked.reduce(
    (total, range) => total + nightHours(range, window),
    0,
  )
  const night = round2(Math.max(computedNight, day.declaredNightHours))

  /*
   * Nadgodziny dobowe liczymy od rozkładu (art. 151 § 1), ale nie poniżej
   * deklaracji: firma, która ewidencjonuje tylko nadgodziny, ma w danych mniej
   * godzin pracy niż w rozkładzie — i nie może na tym stracić.
   */
  const computed = day.scheduled ? Math.max(0, round2(day.workedHours - plannedHours)) : 0
  const overtimeHours = round2(Math.max(computed, day.declaredOvertimeHours))

  let overtimeRateBp: number | null = null
  if (overtimeHours > 0) {
    /*
     * 100 % należy się także wtedy, gdy w nadgodzinach jest praca nocna —
     * a nie tylko gdy CAŁA zmiana wypadła w nocy. Nie mamy podstaw dzielić
     * nadgodzin na nocne i dzienne bez godzin każdej z nich, więc przy pracy
     * nocnej w tym dniu przyjmujemy stawkę wyższą i mówimy o tym w ADR.
     *
     * Niedziela i święto dają 100 % tylko wtedy, gdy nie są dla pracownika
     * dniem pracy — a przy pracy poza rozkładem to reguła, nie wyjątek.
     */
    const dayOffByLaw = (day.sunday || day.holiday) && day.planned === null
    overtimeRateBp = night > 0 || dayOffByLaw ? OVERTIME_RATE_NIGHT_BP : OVERTIME_RATE_DAY_BP
  }

  /*
   * Godziny niedzieli i święta objęte obowiązkiem z art. 151(11): praca
   * z rozkładu, bo tylko ona nie jest nadgodziną. Odejmujemy nadgodziny dnia,
   * żeby dodatek 100 % nie policzył się dwa razy za tę samą godzinę.
   */
  const sundayHolidayHours =
    day.sunday || day.holiday
      ? round2(Math.max(0, day.workedHours - overtimeHours))
      : 0

  return {
    date: day.date,
    plannedHours,
    workedHours: day.workedHours,
    nightHours: night,
    overtimeHours,
    overtimeRateBp,
    plannedOff: day.scheduled && day.planned === null,
    fromDeclaration: overtimeHours > 0 && day.declaredOvertimeHours > computed,
    clockMissing,
    sunday: day.sunday,
    holiday: day.holiday,
    sundayHolidayHours,
  }
}

export interface PeriodReview {
  readonly days: readonly DayReview[]
  readonly plannedHours: number
  readonly workedHours: number
  readonly nightHours: number
  /** Nadgodziny dobowe razem — z przekroczeń rozkładu w poszczególnych dniach. */
  readonly overtimeDailyHours: number
  /** Nadgodziny z dodatkiem 50 % i 100 % (art. 151(1) § 1). */
  readonly overtimeDayRateHours: number
  readonly overtimeNightRateHours: number
  /**
   * Nadgodziny średniotygodniowe: nadwyżka czasu przepracowanego nad wymiarem
   * okresu, po odjęciu nadgodzin policzonych już dobowo. Zawsze płatne
   * z dodatkiem 100 % (art. 151(1) § 2).
   */
  readonly overtimeWeeklyHours: number
  /** Dni z pracą bez godzin „od–do" — granica liczenia pory nocnej. */
  readonly clockMissingDays: number
  /**
   * Dni pracy w niedzielę i święto z godzinami objętymi obowiązkiem z art.
   * 151(11) § 1 — wejście do rejestru dni wolnych i do dodatku z § 2 i § 3.
   */
  readonly sundayWorkDays: readonly SundayWorkDay[]
  /** Wszystkie niedziele okresu ze znacznikiem pracy — art. 151(12) k.p. */
  readonly sundays: readonly { readonly date: string; readonly worked: boolean }[]
}

export function reviewPeriod(
  days: readonly WorkedDay[],
  window: TimeRange,
  normHours: number,
): PeriodReview {
  const reviewed = days.map((day) => reviewDay(day, window))
  const sum = (pick: (day: DayReview) => number): number =>
    round2(reviewed.reduce((total, day) => total + pick(day), 0))

  const overtimeDailyHours = sum((day) => day.overtimeHours)
  const workedHours = sum((day) => day.workedHours)
  return {
    days: reviewed,
    plannedHours: sum((day) => day.plannedHours),
    workedHours,
    nightHours: sum((day) => day.nightHours),
    overtimeDailyHours,
    overtimeDayRateHours: sum((day) =>
      day.overtimeRateBp === OVERTIME_RATE_DAY_BP ? day.overtimeHours : 0,
    ),
    overtimeNightRateHours: sum((day) =>
      day.overtimeRateBp === OVERTIME_RATE_NIGHT_BP ? day.overtimeHours : 0,
    ),
    overtimeWeeklyHours: Math.max(0, round2(workedHours - normHours - overtimeDailyHours)),
    clockMissingDays: reviewed.filter((day) => day.clockMissing).length,
    /*
     * Niedziele i święta z pracą z rozkładu (Etap 49). Powód bierzemy z faktu:
     * święto przed niedzielą, bo termin z art. 151(11) § 1 pkt 2 jest inny niż
     * z pkt 1.
     */
    sundayWorkDays: reviewed
      .filter((day) => day.sundayHolidayHours > 0)
      .map((day) => ({
        date: day.date,
        reason: dayOffReason(day),
        hours: day.sundayHolidayHours,
      })),
    sundays: reviewed
      .filter((day) => day.sunday)
      .map((day) => ({ date: day.date, worked: day.workedHours > 0 })),
  }
}

/**
 * Zestawienie osoby spoza stosunku pracy (fala 7b, #35; D102).
 *
 * Norma, nadgodziny, pora nocna i praca w niedziele to instytucje Kodeksu
 * pracy (art. 128–151(12)) — dotyczą pracownika z art. 22 § 1, a nie
 * zleceniobiorcy ani wykonawcy dzieła. Zostają godziny faktycznie
 * przepracowane (przy zleceniu to podstawa stawki godzinowej z art. 8a
 * ustawy o minimalnym wynagrodzeniu) i dni bez zegara; wszystko, co wynika
 * z rozkładu albo z dodatków, jest zerem, a nie „nie wiadomo".
 */
export function civilContractReview(review: PeriodReview): PeriodReview {
  return {
    ...review,
    days: review.days.map((day) => ({
      ...day,
      plannedHours: 0,
      nightHours: 0,
      overtimeHours: 0,
      overtimeRateBp: null,
      plannedOff: false,
      fromDeclaration: false,
      sundayHolidayHours: 0,
    })),
    plannedHours: 0,
    nightHours: 0,
    overtimeDailyHours: 0,
    overtimeDayRateHours: 0,
    overtimeNightRateHours: 0,
    overtimeWeeklyHours: 0,
    sundayWorkDays: [],
    sundays: [],
  }
}

/**
 * Wymiar czasu pracy jako ułamek nieskracalny („1/1", „3/4", „1/2") — tak
 * wymaga go raport imienny ZUS i tak się go zwyczajowo pisze w dokumentach
 * kadrowych („1/2 etatu"). Etat 0,75 to „3/4", nie „0.75".
 */
export function workingTimeFraction(fte: number): string {
  const hundredths = Math.max(0, Math.min(100, Math.round(fte * 100)))
  if (hundredths === 0) return '0/1'
  const divide = (a: number, b: number): number => (b === 0 ? a : divide(b, a % b))
  const common = divide(hundredths, 100)
  return `${hundredths / common}/${100 / common}`
}
